import type {
  Activity,
  MessageActivity,
  MessageActivityDetails,
  NoteActivityDetails,
  PaymentActivityDetails,
  ViewedActivityDetails
} from '@paintscout/api'
// import { getEmailTemplates } from '@paintscout/util/builder'
import type { OptionsDocument } from 'paintscout'
import { getCrews } from '../../builder/options/get-crews'
import { getEmailTemplates } from '../../builder/options/get-email-templates'
import { removeAuth0Prefix } from '../../users/removeAuth0Prefix'

export type ActivityVariant = 'user' | 'customer' | 'automation' | 'other-user' | 'automated-customer'

export function getActivityVariant(activity: Activity, options?: OptionsDocument): ActivityVariant {
  // `options` is undefined until the options document loads — admin renders the activity feed
  // before it resolves. Skipping the lookup falls through to the owner comparison below, which is
  // right more often than the 'other-user' an empty crew list would always produce.
  if ((activity.details as any).crew && options) {
    const crewOptions = getCrews({ options, searchCrewValue: '' })
    const crew = crewOptions.find((crew) => crew._id === (activity.details as any).crew)
    if (removeAuth0Prefix(crew?.projectManager?.id) !== removeAuth0Prefix(activity.details?.owner?.id)) {
      return 'other-user'
    }
  }

  const ownerMismatch =
    !!activity?.details?.owner?.id &&
    !!activity?.details?.quoteOwner?.id &&
    removeAuth0Prefix(activity.details.owner.id) !== removeAuth0Prefix(activity.details.quoteOwner.id)

  switch (activity.details?.activityType) {
    case 'message': {
      const isCustomer = (activity?.details as MessageActivityDetails)?.message?.from?.isCustomer
      const isAutomated = (activity?.details as MessageActivityDetails)?.message?.from?.isAutomated
      if (!isAutomated && !isCustomer && !ownerMismatch) {
        return getLegacyMessageActivityVariant(activity as MessageActivity, options)
      }
      return isAutomated ? 'automation' : isCustomer ? 'customer' : ownerMismatch ? 'other-user' : 'user'
    }
    // Declining only exists as a customer-view mutation (`declineQuote` reads
    // `context.customerView.urlDoc` unconditionally), so a declined activity is always the
    // customer's action. Marking a quote declined from the app writes `status-changed` instead.
    case 'declined':
      return 'customer'
    // `viewed` and `downloaded` are written for staff views too — addGetActivity picks
    // `dates.staff.viewed` for `report.to_staff` and work-order views, but the activity type is
    // the same. Key off the recorded viewer instead of the type.
    case 'viewed':
    case 'downloaded':
      return isContactViewer(activity) ? 'customer' : ownerMismatch ? 'other-user' : 'user'
    // `acceptQuote` serves both the customer view and the estimator's "accept on the spot" flow.
    // It records which one on the quote as `meta.status.acceptedVia`, but only after the activity
    // is written, and the activity keeps no copy — so the two cannot be told apart from here. An
    // on-the-spot accept is therefore still labelled as the customer, which is what this returned
    // before 57970ef2bb removed the case.
    case 'accepted':
      return 'customer'
    // `provider` is set only by the online-payment paths (the Stripe/Payrix webhooks,
    // `createPaymentActivity` and `add-processing-payment`). `getPaymentActivities`, the in-app
    // "record payment" flow, leaves it unset.
    case 'payment':
      return (activity?.details as PaymentActivityDetails)?.provider
        ? 'customer'
        : ownerMismatch
          ? 'other-user'
          : 'user'
    case 'thank-you-sent':
    case 'declined-sent':
    case 'payment-received-sent':
    case 'follow-up-sent':
    case 'event-email-sent':
      return 'automation'
    case 'note': {
      const versions = (activity.details as NoteActivityDetails)?.versions ?? []
      if (versions.length > 0) {
        const latestVersion = versions[0]
        if (removeAuth0Prefix(latestVersion?.owner?.id) !== removeAuth0Prefix(activity.details?.owner?.id)) {
          return 'other-user'
        }
      }
      return 'user'
    }
    case 'lead-form-submission':
      return 'automated-customer'
    case 'deal-created':
    case 'deal-stage-changed': {
      // An automation on an ownerless deal (e.g. from a lead form) has no user to draw (ENG-2301)
      if (activity.details?.isAutomated && !activity.details?.owner && !activity.details?.quoteOwner) {
        return 'automated-customer'
      }
      // No break here, we want it to fall through to default case if a user or owner is known
    }
    default: {
      const isAutomated = activity.details?.isAutomated
      return isAutomated ? 'automation' : ownerMismatch ? 'other-user' : 'user'
    }
  }
}

/**
 * True when the email recorded on a `viewed`/`downloaded` activity is the contact's own — i.e. the
 * customer opened the link rather than a staff member.
 *
 * `createQuoteViewedActivity` stores the address as `details.viewingEmail`;
 * `createQuoteDownloadedActivity` has no such field and puts it in `details.events[0]`. Both fields
 * survive the Elasticsearch projection (`getActivityIndexDoc`), so this works on historical
 * documents without a migration. `QuoteViewedActivity` already makes the same comparison to decide
 * whether to render the contact's name in place of the raw email.
 *
 * A view with no recorded email is treated as staff, so an unattributable view keeps the estimator
 * avatar rather than being claimed for the customer.
 */
function isContactViewer(activity: Activity): boolean {
  const contactEmail = activity.details?.contact?.email
  if (!contactEmail) {
    return false
  }

  const events = activity.details?.events
  // `events` is normally an array, but activitiesForDoc has to defend against a bare string
  // coming back from Elasticsearch, so do the same here.
  const eventEmail = Array.isArray(events) ? events[0] : events
  const viewerEmail = (activity.details as ViewedActivityDetails)?.viewingEmail ?? eventEmail

  if (typeof viewerEmail !== 'string' || !viewerEmail) {
    return false
  }

  return viewerEmail.trim().toLowerCase() === contactEmail.trim().toLowerCase()
}

function getLegacyMessageActivityVariant(activity: MessageActivity, options?: OptionsDocument): ActivityVariant {
  const emailTemplates = getEmailTemplates({ options })
  const automaticTemplates = [
    emailTemplates['accept-thank-you'],
    emailTemplates['decline-thank-you'],
    emailTemplates['payment-received']
  ]
    .map((template) => template?.body)
    .filter(Boolean)

  const messageContent = activity.details?.message?.text

  // Try to match messageContent to any template
  const isAutomated = automaticTemplates.some((body) => {
    if (!body) {
      return false
    }
    const regex = templateToRegex(body)
    return regex.test(messageContent)
  })

  return isAutomated ? 'automation' : 'user'
}

function templateToRegex(template: string): RegExp {
  try {
    // Escape all regex special characters, including curly braces
    const escaped = template.replaceAll(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
    // Replace escaped {{...}} with a non-greedy wildcard
    const pattern = escaped.replaceAll(/\\\{\\\{.*?\\\}\\\}/g, '.*?')
    // Match the entire string, case-insensitive
    // eslint-disable-next-line security/detect-non-literal-regexp
    return new RegExp(`^${pattern}$`, 'i')
    // eslint-disable-next-line unused-imports/no-unused-vars
  } catch {
    return /$^/ // Always-failing regex
  }
}
